import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuPayload, MenuService } from '../menu.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';
 
@Component({
  selector: 'app-main-menus',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './main-menus.component.html',
  styleUrl: './main-menus.component.css',
})
export class MainMenusComponent implements OnInit {
  menus: any[] = [];

  // --- UAC action: map permission buttons from template ---
  openUacActionEdit(permission: any) {
    // Your API expects: master/uac/action/${id}
    // Sample response provided by you: { id: 6, ... }
    const uacActionId = Number(permission?.id);
    if (!uacActionId) return;

    this.menuService.getUacActionById(uacActionId).subscribe({
      next: (res: any) => {
        const action = res?.body?.data ?? res?.body ?? res?.body?.data;
        // Navigate to the uac-actions page with the loaded action.
        // uac-actions.component.ts already calls getUacActionById/updateUacAction.
        this.router.navigate(['/uac-actions'], {
          state: { editingAction: action },
        });
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to load UAC action details',
        );
      },
    });
  }

  openUacActionDelete(permission: any) {
    const uacActionId = Number(permission?.id);
    if (!uacActionId) return;

    if (!confirm('Delete this UAC action?')) return;

    this.menuService.deleteUacAction(uacActionId).subscribe({
      next: (res: any) => {
        this.toasterService.setToaster(
          'success',
          'Deleted',
          res?.body?.message ?? 'UAC action deleted successfully',
        );
        this.fetchMenus();
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to delete UAC action',
        );
      },
    });
  }



  // Max character validation
  readonly MAX_MENU_NAME = 50;
  readonly MAX_SLUG = 100;
  readonly MAX_URL = 200;

  maxCharError: string | null = null;
  isFormValid = true;

 
  expandedParent: number | null = null;
  selectedActions: any[] = [];
  expandedPermissions: number[] = [];
 
  editingMenu: any = null;
  isCreate = false;
 
  constructor(
    private menuService: MenuService,
    private toasterService: ToasterService,
    private router: Router,
  ) {}
 
  ngOnInit() {
    this.fetchMenus();
  }
 
  /* ================= FETCH ================= */
 
  fetchMenus() {
    this.menuService
      .getMenus()
      .subscribe({
        next: (res: any) => {
          this.menus = res?.body?.data || [];
        },
        error: () => {
          this.toasterService.setToaster(
            'error',
            'Error',
            'Failed to load menus',
          );
        },
      })
  }
 
  /* ================= VIEW MENU ================= */
 
  // Expands a menu node in the UI table (used by the row action buttons)
  viewMenu(menuId: number) {
    const menu = this.menus.find((m) => m.id === menuId);
    if (!menu) return;

    this.expandedParent = this.expandedParent === menuId ? null : menuId;
    this.selectedActions = this.flattenMenuTree(menu);
  }
 
  togglePermissions(itemId: number) {
    if (this.expandedPermissions.includes(itemId)) {
      this.expandedPermissions = this.expandedPermissions.filter(id => id !== itemId);
    } else {
      this.expandedPermissions.push(itemId);
    }
  }
 
  /* ================= MASTER FLATTENER ================= */
 
  flattenMenuTree(menu: any): any[] {
    let result: any[] = [];
 
    // Handle children (recursive)
    if (menu.children?.length) {
      result = result.concat(
        this.flattenChildren(menu.children, 0, menu.menu_name),
      );
    }

    return result;
  }

  /* ================= CHILDREN ================= */

  flattenChildren(children: any[], level: number, parentName: string): any[] {
    let output: any[] = [];

    children.forEach((child) => {
      output.push({
        ...child,
        type: 'submenu',
        _level: level,
        _parentName: parentName,
        is_active: child.is_active || true, // Default to active if not provided
      });

      if (child.children?.length) {
        output = output.concat(
          this.flattenChildren(child.children, level + 1, child.menu_name),
        );
      }
    });

    return output;
  }
 
  /* ================= CREATE / EDIT ================= */
 
  openCreate() {
    this.isCreate = true;
    this.editingMenu = {
      menu_name: '',
      slug: '',
      parent_menu_id: null,
      url: '',
      type: 'side',
      sequence_no: this.menus.length + 1,
      is_active: true,
    };
  }
 
  openEdit(menu: any) {
    this.isCreate = false;
    this.menuService.getMenuById(menu.id).subscribe({
      next: (res: any) => {
        const menuDetails = res?.body?.data ?? res?.body ?? menu;
        this.editingMenu = this.normalizeMenuForEdit(menuDetails);
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to load menu details',
        );
      },
    });
  }
 
  saveEdit() {
    this.maxCharError = null;
    this.isFormValid = true;

    if (!this.validateMaxCharacters()) {
      this.isFormValid = false;
      return;
    }

    const payload = this.buildMenuPayload();
    const request$ = this.isCreate
      ? this.menuService.createMenu(payload)
      : this.menuService.updateMenu(this.editingMenu.id, payload);

    request$.subscribe({
      next: (res: any) => {
        this.toasterService.setToaster(
          'success',
          'Success',
          res?.body?.message ?? `Menu ${this.isCreate ? 'created' : 'updated'} successfully`,
        );
        this.editingMenu = null;
        this.fetchMenus();
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? `Failed to ${this.isCreate ? 'create' : 'update'} menu`,
        );
      },
    });
  }
 
  cancelEdit() {
    this.editingMenu = null;
  }

  deleteMenu(menu: any) {
    if (!confirm(`Delete ${menu.menu_name}?`)) {
      return;
    }

    this.menuService.deleteMenu(menu.id).subscribe({
      next: (res: any) => {
        this.toasterService.setToaster(
          'success',
          'Deleted',
          res?.body?.message ?? 'Menu deleted successfully',
        );
        this.fetchMenus();
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to delete menu',
        );
      },
    });
  }

  deleteSubMenu(item: any) {
    const menuName = item?.menu_name || item?.name || 'submenu';
    if (!confirm(`Delete ${menuName}?`)) {
      return;
    }

    this.menuService.deleteMainMenuSubmenu(item.id).subscribe({
      next: (res: any) => {
        this.toasterService.setToaster(
          'success',
          'Deleted',
          res?.body?.message ?? 'Sub menu deleted successfully',
        );
        this.fetchMenus();
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to delete sub menu',
        );
      },
    });
  }


  openUacActions() {
    this.router.navigate(['/uac-actions']);
  }

  private buildMenuPayload(): MenuPayload {
    return {
      menu_name: this.editingMenu.menu_name,
      slug: this.editingMenu.slug,
      parent_menu_id: this.editingMenu.parent_menu_id || null,
      url: this.editingMenu.url,
      type: this.editingMenu.type,
      sequence_no: Number(this.editingMenu.sequence_no),
      is_active: this.editingMenu.is_active ? 1 : 0,
    };
  }

  private normalizeMenuForEdit(menu: any) {
    return {
      ...menu,
      slug: menu.slug ?? '',
      parent_menu_id: menu.parent_menu_id ?? menu.parent_id ?? null,
      is_active: menu.is_active === 1 || menu.is_active === true,
    };
  }

  private validateMaxCharacters(): boolean {
    const menuName = (this.editingMenu?.menu_name ?? '').toString();
    const slug = (this.editingMenu?.slug ?? '').toString();
    const url = (this.editingMenu?.url ?? '').toString();

    if (menuName.length > this.MAX_MENU_NAME) {
      this.maxCharError = `Menu Name must be at most ${this.MAX_MENU_NAME} characters.`;
      return false;
    }

    if (slug.length > this.MAX_SLUG) {
      this.maxCharError = `Slug must be at most ${this.MAX_SLUG} characters.`;
      return false;
    }

    if (url.length > this.MAX_URL) {
      this.maxCharError = `URL must be at most ${this.MAX_URL} characters.`;
      return false;
    }

    return true;
  }
}

 
 
